/**
 * SDD Monitoring — for the Compliance Saarthi platform team.
 *
 * This screen answers "is the product healthy for this client". It never shows
 * what a client's register says: no nature of UPSI, no names, no PANs, no
 * purposes. A client's SDD is the list of who knew price-sensitive information
 * before the market did, and a vendor with a permanent window into it is the
 * exposure the regulation exists to prevent.
 *
 * There is no "view register" control here, and the API has no route that would
 * serve one. If support genuinely needs client data, the company grants it from
 * their own screen — please do not add a shortcut.
 */

import { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import { TbRefresh, TbShieldCheck, TbShieldX, TbAlertTriangle, TbLock, TbMail } from 'react-icons/tb';
import { Field, TextField } from '../components/common/FormControls';
import { useUI } from '../context/UIContext';
import { sddAdminAPI } from '../services/api';

const fmt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${d
    .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-[#e8e2d6] p-5 ${className}`}>{children}</div>
);

export default function AdminSddMonitoringPage() {
  const { showToast } = useUI();

  const [companies, setCompanies] = useState([]);
  const [totals, setTotals] = useState(null);
  const [encryption, setEncryption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAdminAPI.getCompanies();
      setCompanies(res?.data?.companies || []);
      setTotals(res?.data?.totals || null);
      setEncryption(res?.data?.encryption || null);
    } catch (error) {
      showToast(error?.message || 'Could not load SDD monitoring.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (companyId) => {
    try {
      const res = await sddAdminAPI.getCompany(companyId);
      setDetail(res?.data || null);
    } catch (error) {
      showToast(error?.message || 'Could not load company detail.', 'error');
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="SDD Monitoring"
        subtitle="Service health across client registers — counts and status only, never register content"
        actions={
          <Button variant="outline" size="sm" leftIcon={<TbRefresh />} onClick={load} loading={loading}>
            Refresh
          </Button>
        }
      />

      {encryption && !encryption.ok && (
        <Card className="mb-4 border-[#9a564a]">
          <div className="flex items-start gap-2 text-[13px] text-[#9a564a]">
            <TbAlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <strong>Identifier encryption is not configured.</strong>
              <div className="text-[12px] mt-0.5">
                {encryption.errors?.join(' ')} Until this is fixed, clients cannot store PANs —
                the API returns 503 rather than storing anything unencrypted.
              </div>
            </div>
          </div>
        </Card>
      )}

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Companies with a register', value: totals.companies },
            { label: 'Register entries', value: totals.entries },
            { label: 'Under legal hold', value: totals.withOpenHold },
            { label: 'Failing integrity', value: totals.failingIntegrity, alert: totals.failingIntegrity > 0 },
          ].map((tile) => (
            // Body font, not the serif: Cormorant's old-style figures render a
            // count of 1 as an I and 0 as an O.
            <Card key={tile.label} className="!p-4">
              <div
                className={`text-[26px] font-bold leading-none tabular-nums ${tile.alert ? 'text-[#9a564a]' : ''}`}
              >
                {tile.value}
              </div>
              <div className="text-[12px] text-[#7a7060] mt-1.5">{tile.label}</div>
            </Card>
          ))}
        </div>
      )}

      {loading ? (
        <Card><div className="text-center py-8 text-[13px] text-[#7a7060]">Loading…</div></Card>
      ) : companies.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-[13px] text-[#7a7060]">
            No client has recorded SDD activity yet.
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.6px] text-[#7a7060] border-b border-[#e8e2d6]">
                <th className="py-2.5 pr-3">Company</th>
                <th className="py-2.5 pr-3">SDD</th>
                <th className="py-2.5 pr-3">Entries</th>
                <th className="py-2.5 pr-3">Insiders</th>
                <th className="py-2.5 pr-3">Events</th>
                <th className="py-2.5 pr-3">Integrity</th>
                <th className="py-2.5 pr-3">Hold</th>
                <th className="py-2.5 pr-3">Last activity</th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {companies.map((row) => (
                <tr key={row.companyId} className="border-b border-[#f0ece2]">
                  <td className="py-3 pr-3">
                    <div className="font-semibold">{row.name}</div>
                    <div className="text-[11px] text-[#a09684]">{row.cin}</div>
                  </td>
                  <td className="py-3 pr-3">
                    {row.sddEnabled
                      ? <Chip tone="#4a6b3f">Enabled</Chip>
                      : <Chip tone="#a09684">Off</Chip>}
                  </td>
                  <td className="py-3 pr-3 tabular-nums">{row.entries}</td>
                  <td className="py-3 pr-3 tabular-nums">{row.insiders}</td>
                  <td className="py-3 pr-3 tabular-nums">{row.events}</td>
                  <td className="py-3 pr-3">
                    {!row.integrity ? (
                      <span className="text-[#a09684]">—</span>
                    ) : row.integrity.ok ? (
                      <span className="text-[#4a6b3f] inline-flex items-center gap-1">
                        <TbShieldCheck size={15} /> Intact
                      </span>
                    ) : (
                      <span className="text-[#9a564a] inline-flex items-center gap-1">
                        <TbShieldX size={15} /> {row.integrity.failureCount} failure(s)
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-3">
                    {row.openHolds > 0
                      ? <Chip tone="#9a564a">{row.openHolds}</Chip>
                      : <span className="text-[#a09684]">—</span>}
                  </td>
                  <td className="py-3 pr-3 whitespace-nowrap text-[#7a7060]">{fmt(row.lastActivityAt)}</td>
                  <td className="py-3 text-right">
                    <Button variant="ghost" size="xs" onClick={() => openDetail(row.companyId)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <Card className="mt-4">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-[15px] font-bold">{detail.company.name}</div>
              <div className="text-[12px] text-[#7a7060]">{detail.company.cin}</div>
            </div>
            <Button variant="ghost" size="xs" onClick={() => setDetail(null)}>Close</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
            <div><div className="text-[11px] text-[#7a7060]">Register entries</div>{detail.counts.entries}</div>
            <div><div className="text-[11px] text-[#7a7060]">Insiders</div>{detail.counts.insiders}</div>
            <div><div className="text-[11px] text-[#7a7060]">UPSI events</div>{detail.counts.events}</div>
            <div>
              <div className="text-[11px] text-[#7a7060]">Integrity</div>
              {detail.integrity
                ? (detail.integrity.ok ? 'Intact' : `${detail.integrity.failureCount} failure(s)`)
                : 'No entries'}
            </div>
            <div>
              <div className="text-[11px] text-[#7a7060]">Encryption</div>
              {detail.encryption?.ok ? 'Healthy' : 'Not configured'}
            </div>
            <div>
              <div className="text-[11px] text-[#7a7060]">Last activity</div>
              {fmt(detail.lastActivityAt)}
            </div>
            <div className="col-span-2">
              <div className="text-[11px] text-[#7a7060]">Retention</div>
              {detail.retention.summary}
            </div>
          </div>

          {detail.integrity && !detail.integrity.ok && (
            <div className="mt-4 text-[12px] text-[#9a564a]">
              Failing rows: {detail.integrity.failedAt.map((f) => `#${f.seq} (${f.type})`).join(', ')}.
              The client’s Compliance Officer should be told; the platform cannot see or repair the
              content.
            </div>
          )}

          {detail.legalHolds.length > 0 && (
            <div className="mt-4">
              <div className="text-[12px] font-semibold mb-1">Legal holds</div>
              {detail.legalHolds.map((hold, i) => (
                <div key={i} className="text-[12px] text-[#7a7060]">
                  {hold.reference} · received {fmt(hold.receivedAt)} ·{' '}
                  {hold.open ? 'open' : `released ${fmt(hold.releasedAt)}`}
                </div>
              ))}
            </div>
          )}

          <MailboxSection companyId={detail.company.id} companyName={detail.company.name} showToast={showToast} />

          <div className="mt-4 pt-3 border-t border-[#f0ece2] flex items-start gap-2 text-[11px] text-[#a09684]">
            <TbLock size={14} className="shrink-0 mt-0.5" />
            <span>
              Register content — the nature of the UPSI, who shared it, who received it and their
              identifiers — is not available to the platform team and is not served by this API.
            </span>
          </div>
        </Card>
      )}
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  UPSI MAILBOX — connected here, once, at onboarding         */
/* ═══════════════════════════════════════════════════════════ */

const MAILBOX_STATUS = {
  never_connected: { label: 'Not tested yet', tone: '#8a6a1f' },
  ok: { label: 'Connected', tone: '#4a6b3f' },
  error: { label: 'Error', tone: '#9a564a' },
  disabled: { label: 'Switched off', tone: '#a09684' },
};

const emptyMailboxForm = { upsiAddress: '', host: '', port: 993, secure: true, user: '', folder: 'INBOX', password: '', enabled: true };

/**
 * The one client-owned thing set from the admin panel, and the exception is
 * deliberate: the client hands the UPSI mailbox login to the ComplyV team once,
 * so no screen their 400 insiders can reach ever shows a mail-password form.
 * The password is encrypted on save and never returned; every connect, test
 * and read is audited under the admin's name.
 */
function MailboxSection({ companyId, companyName, showToast }) {
  const [mailbox, setMailbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyMailboxForm);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAdminAPI.getMailbox(companyId);
      setMailbox(res?.data?.mailbox || null);
    } catch (error) {
      showToast(error?.message || 'Could not load the mailbox.', 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => { setEditing(false); load(); }, [load]);

  const startEdit = () => {
    setForm(mailbox
      ? { upsiAddress: mailbox.upsiAddress, host: mailbox.imap.host, port: mailbox.imap.port, secure: mailbox.imap.secure, user: mailbox.imap.user, folder: mailbox.imap.folder || 'INBOX', password: '', enabled: mailbox.enabled }
      : emptyMailboxForm);
    setEditing(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = async (kind, call, after) => {
    setBusy(kind);
    try {
      const res = await call();
      after?.(res);
      showToast(res?.message || 'Done.', 'success');
    } catch (error) {
      showToast(error?.message || 'Something went wrong.', 'error');
      load();
    } finally {
      setBusy('');
    }
  };

  const save = () => run('save', () => sddAdminAPI.setMailbox(companyId, { ...form, port: Number(form.port) }), (res) => { setMailbox(res?.data?.mailbox || null); setEditing(false); });
  const test = () => run('test', () => sddAdminAPI.testMailbox(companyId), () => load());
  const sync = () => run('sync', () => sddAdminAPI.syncMailbox(companyId), (res) => setMailbox(res?.data?.mailbox || mailbox));

  if (loading) return null;
  const status = MAILBOX_STATUS[mailbox?.status] || MAILBOX_STATUS.never_connected;

  return (
    <div className="mt-4 pt-4 border-t border-[#f0ece2]">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <TbMail className="text-[#8a6a1f]" />
            <span className="text-[13px] font-semibold">UPSI mailbox</span>
            {mailbox ? <Chip tone={status.tone}>{status.label}</Chip> : <Chip tone="#a09684">Not connected</Chip>}
          </div>
          {mailbox ? (
            <div className="text-[12px] text-[#7a7060] leading-relaxed">
              <span className="font-mono text-[#4a4034]">{mailbox.upsiAddress}</span> via {mailbox.imap.host}:{mailbox.imap.port} ·
              folder {mailbox.imap.folder} · {mailbox.observed} email{mailbox.observed === 1 ? '' : 's'} read
              {mailbox.lastSyncAt && <> · last read {fmt(mailbox.lastSyncAt)}</>}
              {mailbox.status === 'error' && mailbox.lastError && (
                <div className="mt-1 text-[#9a564a]">Last error: {mailbox.lastError}</div>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-[#7a7060] leading-relaxed max-w-2xl">
              {companyName} has no UPSI mailbox connected. Enter the details the client shared for their
              dedicated address (e.g. upsi@theircompany.com). The password is stored encrypted and never shown again.
            </div>
          )}
        </div>
        {!editing && (
          <div className="flex gap-2 flex-wrap">
            {mailbox && mailbox.enabled && (
              <>
                <Button variant="outline" size="sm" onClick={test} loading={busy === 'test'} disabled={!!busy && busy !== 'test'}>Test connection</Button>
                <Button variant="outline" size="sm" onClick={sync} loading={busy === 'sync'} disabled={!!busy && busy !== 'sync'}>Read now</Button>
              </>
            )}
            <Button size="sm" onClick={startEdit} disabled={!!busy}>{mailbox ? 'Settings' : 'Connect mailbox'}</Button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="UPSI address (the one insiders copy)" required className="md:col-span-2">
              <TextField type="email" value={form.upsiAddress} onChange={set('upsiAddress')} placeholder="upsi@theircompany.com" />
            </Field>
            <Field label="Mail server (IMAP)" required>
              <TextField value={form.host} onChange={set('host')} placeholder="outlook.office365.com · imap.gmail.com · imap.zoho.in" />
            </Field>
            <Field label="Mailbox login" required>
              <TextField value={form.user} onChange={set('user')} placeholder="Usually the same as the address" autoComplete="off" />
            </Field>
            <Field label={mailbox?.hasPassword ? 'Password (leave blank to keep the current one)' : 'Password'} required={!mailbox?.hasPassword}>
              <TextField type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
            </Field>
            <Field label="Folder">
              <TextField value={form.folder} onChange={set('folder')} placeholder="INBOX" />
            </Field>
            <Field label="Port" required>
              <TextField type="number" value={form.port} onChange={set('port')} />
            </Field>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.secure} onChange={set('secure')} className="h-4 w-4 accent-[#145f5f]" /> Use TLS (port 993)
              </label>
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.enabled} onChange={set('enabled')} className="h-4 w-4 accent-[#145f5f]" /> Read this mailbox
              </label>
            </div>
          </div>
          <div className="text-[11px] text-[#7a7060] mt-3 leading-relaxed">
            These are the details of the client's UPSI mailbox only — never an insider's. Microsoft 365 and Google
            mailboxes need an app password (a normal login password is usually refused). Take the password by phone or
            password manager, never by email or chat, and ask the client to rotate it after connection.
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={save} loading={busy === 'save'} disabled={!!busy && busy !== 'save'}>Save</Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={!!busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
